import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMail, FiPhone, FiMapPin, FiClock } from 'react-icons/fi';
import { contactEmails, contactPhones, contactInfo, officeHours, contactContent, contactFormConfig } from '../../data';
import './Contact.css';

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const officeAddress = contactInfo.find((info) => info.title === 'Office');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert(contactFormConfig.successMessage);
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: '',
    });
  };

  return (
    <section id="contact" className="contact section">
      <div className="container">
        {/* Header */}
        <motion.div
          className="contact-header"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
        >
          <span className="section-eyebrow">{contactContent.label}</span>
          <h2 className="section-heading">
            {contactContent.title.split(contactContent.titleHighlight)[0]}
            <span className="accent">{contactContent.titleHighlight}</span>
          </h2>
          <p className="contact-subtitle">{contactContent.subtitle}</p>
        </motion.div>

        <div className="contact-panel">
          {/* Info side — dark */}
          <motion.aside
            className="contact-info"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <h3 className="contact-info-title">Contact Information</h3>
            <p className="contact-info-text">
              Reach out directly or visit our office — our team is ready to discuss your project.
            </p>

            <ul className="contact-details">
              {contactEmails.map((item) => (
                <li key={`email-${item.id}`}>
                  <span className="detail-icon"><FiMail /></span>
                  <div>
                    <span className="detail-label">{item.label}</span>
                    <a href={item.link} className="detail-value">{item.email}</a>
                  </div>
                </li>
              ))}
              {contactPhones.map((item) => (
                <li key={`phone-${item.id}`}>
                  <span className="detail-icon"><FiPhone /></span>
                  <div>
                    <span className="detail-label">{item.label}</span>
                    <a href={item.link} className="detail-value">{item.phone}</a>
                  </div>
                </li>
              ))}
              {officeAddress && (
                <li>
                  <span className="detail-icon"><FiMapPin /></span>
                  <div>
                    <span className="detail-label">Office</span>
                    <span className="detail-value">{officeAddress.content}</span>
                  </div>
                </li>
              )}
            </ul>

            <div className="contact-hours">
              <h4><FiClock /> {officeHours.title}</h4>
              {officeHours.schedule.map((schedule) => (
                <div key={schedule.days} className="hours-row">
                  <span>{schedule.days}</span>
                  <span>{schedule.hours}</span>
                </div>
              ))}
            </div>
          </motion.aside>

          {/* Form side */}
          <motion.div
            className="contact-form-wrapper"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXXXXXXX"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Project Details *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder="Tell us about your construction project..."
                ></textarea>
              </div>

              <button type="submit" className="btn btn-solid">
                Send Message <span className="btn-arrow">→</span>
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
